import { useAtmosphereAmbience } from '@/hooks/use-atmosphere-ambience';
import { cn } from '@/lib/utils';

const grainStyle = {
    backgroundImage:
        'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.85\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
};

const quietBlobs = [
    {
        color: 'bg-zinc-500/10',
        size: 'size-96',
        anchor: { top: '10%', right: '-8rem' },
        animationName: 'atmosphere-drift-1',
        duration: '28s',
        delay: '0s',
    },
    {
        color: 'bg-zinc-600/10',
        size: 'size-80',
        anchor: { bottom: '15%', left: '-7rem' },
        animationName: 'atmosphere-drift-2',
        duration: '34s',
        delay: '4s',
    },
] as const;

type AtmosphereBackgroundProps = {
    className?: string;
    fixed?: boolean;
    variant?: 'default' | 'subtle' | 'quiet';
    withGrain?: boolean;
};

export function AtmosphereBackground({
    className,
    fixed = true,
    variant = 'default',
    withGrain = false,
}: AtmosphereBackgroundProps) {
    const ambience = useAtmosphereAmbience();
    const position = fixed ? 'fixed' : 'absolute';
    const isSubtle = variant === 'subtle';
    const isQuiet = variant === 'quiet';
    const radial = isQuiet ? 'from-zinc-500/10' : ambience.radial;
    const blobs = isQuiet ? quietBlobs : ambience.blobs;

    return (
        <>
            <div
                aria-hidden
                className={cn(
                    position,
                    'inset-0 z-0 overflow-hidden pointer-events-none',
                    className,
                )}
            >
                <div
                    className={cn(
                        'absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))]',
                        radial,
                        'via-background to-background',
                        isSubtle && 'opacity-70',
                    )}
                />
                {blobs.map((blob, index) => (
                    <div
                        key={index}
                        className={cn(
                            'atmosphere-blob absolute rounded-full blur-3xl motion-reduce:blur-2xl',
                            blob.size,
                            blob.color,
                            isSubtle && 'opacity-60',
                        )}
                        style={{
                            top: blob.anchor.top,
                            right: blob.anchor.right,
                            bottom: blob.anchor.bottom,
                            left: blob.anchor.left,
                            animationName: blob.animationName,
                            animationDuration: blob.duration,
                            animationDelay: blob.delay,
                        }}
                    />
                ))}
            </div>
            {withGrain && (
                <div
                    aria-hidden
                    className={cn(
                        position,
                        'inset-0 z-0 pointer-events-none opacity-[0.035] motion-reduce:opacity-0',
                        className,
                    )}
                    style={grainStyle}
                />
            )}
        </>
    );
}
